/* ==========================================================================
   折扣码自动应用 —— 运营 2026-09-16 表行 17
   ==========================================================================
   需求（运营原话）：详情页、购物车抽屉、购物车页面、结账页面**自动 apply 折扣码**。

   码从哪来（不在代码里写死任何折扣码）：
     商品 metafield `pro_info.discount_code` → snippets/amada-coupon-bar.liquid
     把它输出成券条上的 `data-amada-discount-code`（PDP 与 quick view 共用同一份
     markup）。本脚本只认这个 DOM 属性，读不到就什么都不做。

   Shopify 机制（shopify.dev changelog 2025-05-21）：
     · `POST /cart/update.js`  body `{ discount: "A,B" }` —— 逗号分隔，
       **整串覆盖**购物车上的折扣码，所以必须把已生效的码一起带上，否则会把
       顾客手动输入的码顶掉。
     · `GET  /cart.js`         返回 `discount_codes: [{ code, applicable }]`。
     · 购物车级折扣进 checkout 自动带上，不需要在结账页再做任何事。

   触发时机（三处，互不重复请求）：
     1. DOMContentLoaded —— 页面上有券条且购物车非空时补一次。
     2. document 的 `cart:updated` —— 加购/改数量后（theme.js 2987/3045/3143 都会
        派发到 document），此时购物车刚从空变成有货，是真正生效的那一次；
        应用成功后调 `window.AmadaCartDrawer.refresh()`（sections/quick-cart.liquid
        暴露的公开 API）让抽屉金额与折扣行刷新 —— 不去碰 theme.js。
     3. `/cart` 整页 —— 页面上没有券条，码来自本会话记录（sessionStorage）。
        只有「本次真的新应用上了一个码」才 reload 一次，避免死循环。

   🚨 购物车为空时不发 `POST /cart/update.js`：空车挂折扣码 Shopify 不会保留，
      白发一次请求还会污染「反例：没券条的商品页不发 update.js」的验收口径。

   🚨 失败一律只 console.debug，绝不打断页面（加购/结账链路优先）。

   2026-10-05 运营 10.5 表第 11 行补强：「只要 PDP 显示了折扣码，购物车就要自动匹配」。
   原来码只来自「当前页面上的券条」+ 本会话记录 —— 从快速预览、分类页卡片「+」、
   首页卡片这些没有券条的入口加购，码永远挂不上（实测 quick view 显示 AM230，加购后车里无码）。
   现在每次购物车变化都按车里每件商品去查它自己的券码：
     · `GET /products/<handle>?view=amada-coupon`（templates/product.amada-coupon.liquid，
       口径与券条 snippets/amada-coupon-bar.liquid 共用 snippets/amada-coupon-eligible-variants.liquid）
     · 同一会话每个商品只查一次（sessionStorage 缓存）
     · Shopify 判定不可用的码（如已达使用上限）5 分钟内不再重复尝试，避免每次改数量都白发两次请求
   自己用裸 fetch 加购的模块（amada-quickview.js / amada-home-redesign.js）加购成功后
   在 document 上派发 `cart:updated`，走下面同一条触发路径。
   ========================================================================== */
(function () {
  'use strict';

  /* 全站只跑一份：theme.liquid 引一次；万一将来某个 section 又引了一次也不会跑两遍 */
  if (window.__amadaAutoDiscount) return;
  window.__amadaAutoDiscount = true;

  var STORE_KEY = 'amada:auto-discount';
  var RELOAD_KEY = 'amada:auto-discount:reloaded';
  var running = false;

  /* ---------------------------------------------------------------- 工具 */

  function cartBase() {
    var t = window.theme && window.theme.routes && window.theme.routes.cart;
    return (t && t.base) || '/cart';
  }

  function log() {
    if (!window.console || !console.debug) return;
    try {
      console.debug.apply(console, ['[amada-auto-discount]'].concat([].slice.call(arguments)));
    } catch (e) {
      /* 某些浏览器 console.debug 不可 apply，忽略 */
    }
  }

  /** sessionStorage 读写：私密模式/禁用存储时全部降级成「没有记录」，不报错 */
  function readStore() {
    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      var obj = raw ? JSON.parse(raw) : null;
      return obj && typeof obj === 'object' ? obj : {};
    } catch (e) {
      return {};
    }
  }

  function writeStore(obj) {
    try {
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(obj));
    } catch (e) {
      /* 存不下就算了，下次导航重新试一遍，不影响功能正确性 */
    }
  }

  /** 把码记进本会话：state = 'seen'（见过券条）| 'applied'（已确认生效） */
  function remember(codes, state) {
    if (!codes.length) return;
    var store = readStore();
    var changed = false;
    codes.forEach(function (code) {
      var key = code.toUpperCase();
      if (store[key] === 'applied') return; // applied 是终态，不被 seen 降级
      if (store[key] !== state) {
        store[key] = state;
        changed = true;
      }
    });
    if (changed) writeStore(store);
  }

  /** 去重（大小写不敏感，保留首次出现的原始大小写） */
  function dedupe(list) {
    var seen = {};
    var out = [];
    list.forEach(function (raw) {
      var code = String(raw == null ? '' : raw).trim();
      if (!code) return;
      var key = code.toUpperCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push(code);
    });
    return out;
  }

  /* ------------------------------------------------- 按商品查券码（10.5 #11） */

  /* v1 → v2：v1 请求带了 Accept: application/json，Shopify 按内容协商直接回商品自带 JSON
     （不走 ?view=amada-coupon 模板），缓存里存的全是空码，必须作废。 */
  var PRODUCT_KEY = 'amada:auto-discount:products:v2';
  var REJECTED_KEY = 'amada:auto-discount:rejected';
  var REJECT_TTL_MS = 5 * 60 * 1000;
  var lookupInflight = {};

  function readJson(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      var obj = raw ? JSON.parse(raw) : null;
      return obj && typeof obj === 'object' ? obj : {};
    } catch (e) {
      return {};
    }
  }

  function writeJson(key, obj) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(obj));
    } catch (e) {
      /* 存不下就每次重查，功能不受影响 */
    }
  }

  /** 近期被 Shopify 判为不可用的码（5 分钟内不再尝试） */
  function recentlyRejected(code) {
    var map = readJson(REJECTED_KEY);
    var ts = map[String(code).toUpperCase()];
    return typeof ts === 'number' && Date.now() - ts < REJECT_TTL_MS;
  }

  function markRejected(codes) {
    if (!codes.length) return;
    var map = readJson(REJECTED_KEY);
    codes.forEach(function (c) {
      map[String(c).toUpperCase()] = Date.now();
    });
    writeJson(REJECTED_KEY, map);
  }

  /** 查一个商品的券信息：{ code, ok(有金额/比例), eligible('|id||id|') }；查不到返回 null */
  function lookupProduct(handle) {
    if (!handle) return Promise.resolve(null);
    var cached = readJson(PRODUCT_KEY)[handle];
    if (cached) return Promise.resolve(cached);
    if (lookupInflight[handle]) return lookupInflight[handle];
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    /* 🚨 不能带 Accept: application/json —— Shopify 会对 /products/<handle> 做内容协商，
       直接返回商品自带的 JSON，根本不渲染 templates/product.amada-coupon.liquid（实测踩过）。 */
    lookupInflight[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '?view=amada-coupon', {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('lookup ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var d = JSON.parse(text);
        /* 返回的不是我们的模板（没有 code 字段）→ 当查询失败，不写缓存，下次再查 */
        if (!d || typeof d !== 'object' || !Object.prototype.hasOwnProperty.call(d, 'code')) {
          throw new Error('unexpected payload');
        }
        var entry = {
          code: String(d.code || '').trim(),
          ok: !!(d.amount || d.pct),
          eligible: String(d.eligible || '')
        };
        var map = readJson(PRODUCT_KEY);
        map[handle] = entry;
        writeJson(PRODUCT_KEY, map);
        return entry;
      })
      .catch(function (err) {
        log('查券码失败（忽略）', handle, err && err.message);
        return null;
      })
      .then(function (entry) {
        delete lookupInflight[handle];
        return entry;
      });
    return lookupInflight[handle];
  }

  /** 购物车里每件商品各自的券码（与 PDP 券条同口径：有金额 + 当前变体在适用名单里） */
  function cartProductCodes(cart) {
    var items = (cart && cart.items) || [];
    var handles = [];
    items.forEach(function (it) {
      if (it.handle && handles.indexOf(it.handle) === -1) handles.push(it.handle);
    });
    return Promise.all(handles.map(lookupProduct)).then(function (entries) {
      var codes = [];
      items.forEach(function (it) {
        var e = entries[handles.indexOf(it.handle)];
        if (!e || !e.code || !e.ok) return;
        if (e.eligible && e.eligible.indexOf('|' + it.variant_id + '|') === -1) return;
        codes.push(e.code);
      });
      return dedupe(codes);
    });
  }

  /* ------------------------------------------------------------ 码的来源 */

  /**
   * 页面上所有券条里的折扣码。
   * 🚨 跳过 [hidden] 的券条：amada-coupon-bar.liquid 对「当前变体不适用」的券条
   *    加了 hidden，此时这个码不该被自动应用到购物车。
   */
  function collectCodes() {
    var els = document.querySelectorAll('[data-amada-discount-code]');
    var out = [];
    Array.prototype.forEach.call(els, function (el) {
      if (el.hasAttribute('hidden')) return;
      if (el.closest && el.closest('[hidden]')) return;
      out.push(el.getAttribute('data-amada-discount-code'));
    });
    return dedupe(out);
  }

  /**
   * 本会话记录过的码（PDP → /cart 之间靠它传递，购物车页没有券条）。
   * @param {String} [state] 传 'applied' 只取已确认生效的码；不传则取全部记录。
   */
  function storedCodes(state) {
    var store = readStore();
    return dedupe(
      Object.keys(store).filter(function (k) {
        return !state || store[k] === state;
      })
    );
  }

  function isCartPage() {
    var base = cartBase().replace(/\/+$/, '');
    var path = window.location.pathname.replace(/\/+$/, '');
    return path === base;
  }

  /* -------------------------------------------------------------- 主流程 */

  /** GET /cart.js —— 拿 item_count 与当前已挂的折扣码 */
  function fetchCart() {
    return fetch(cartBase() + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('GET cart.js ' + r.status);
      return r.json();
    });
  }

  /** 把 cart.discount_codes 拆成 { applicable: [...], all: [...] }（原始大小写） */
  function splitCartCodes(cart) {
    var list = (cart && cart.discount_codes) || [];
    var applicable = [];
    var all = [];
    list.forEach(function (d) {
      if (!d || !d.code) return;
      all.push(d.code);
      if (d.applicable) applicable.push(d.code);
    });
    return { applicable: applicable, all: all };
  }

  function hasCode(list, code) {
    var key = String(code).toUpperCase();
    return list.some(function (c) {
      return String(c).toUpperCase() === key;
    });
  }

  /**
   * 应用一批折扣码。
   * @param {String[]} codes 想要生效的码
   * @param {Object}   opts  { afterCartChange: Boolean } 是否由 cart:updated 触发
   * @returns Promise<Boolean> 本次是否**新**应用上了至少一个码
   */
  function applyCodes(codes, opts) {
    opts = opts || {};
    if (!codes.length && !opts.resolveFromCart) return Promise.resolve(false);
    if (typeof fetch !== 'function') return Promise.resolve(false);
    if (running) return Promise.resolve(false);
    running = true;

    return fetchCart()
      .then(function (cart) {
        if (!opts.resolveFromCart || !cart || !cart.item_count) return { cart: cart, codes: codes };
        return cartProductCodes(cart).then(function (extra) {
          return { cart: cart, codes: dedupe(codes.concat(extra)) };
        });
      })
      .then(function (ctx) {
        var cart = ctx.cart;
        /* 近期被 Shopify 拒过的码先不试（已在车上的不受影响：current.applicable 照常保留） */
        codes = ctx.codes.filter(function (c) {
          return !recentlyRejected(c);
        });
        if (!codes.length) return false;
        var current = splitCartCodes(cart);

        /* 已经全部生效 → 记账后收工，不再发 POST */
        var missing = codes.filter(function (c) {
          return !hasCode(current.applicable, c);
        });
        remember(
          codes.filter(function (c) {
            return hasCode(current.applicable, c);
          }),
          'applied'
        );
        if (!missing.length) {
          log('已全部生效，无需请求', codes);
          return false;
        }

        /* 空车不挂码：Shopify 不会保留，等 cart:updated 那一次再挂 */
        if (!cart || !cart.item_count) {
          log('购物车为空，暂不应用', missing);
          return false;
        }

        /* 整串覆盖 → 必须把已生效的码一起带上，别顶掉顾客手输的码 */
        var payload = dedupe(current.applicable.concat(codes));
        return fetch(cartBase() + '/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ discount: payload.join(',') })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('POST cart/update.js ' + r.status);
            return r.json();
          })
          .then(function (next) {
            var after = splitCartCodes(next);
            var applied = codes.filter(function (c) {
              return hasCode(after.applicable, c);
            });
            remember(applied, 'applied');
            var gained = applied.filter(function (c) {
              return !hasCode(current.applicable, c);
            });
            log('已应用', applied, '新增', gained);

            /* 我们加上去、结果却不适用的码要撤掉：否则它会挂在购物车上一路带进
               结账页，顾客会看到「折扣码无效」的红字。撤回时保留原本就在车上的
               所有码（含顾客手动输入的），只丢弃本轮新加又没生效的那些。 */
            var junk = codes.filter(function (c) {
              return !hasCode(after.applicable, c) && !hasCode(current.all, c);
            });
            if (!junk.length) return gained.length > 0;
            markRejected(junk);
            var keep = dedupe(current.all.concat(after.applicable));
            log('撤回不适用的码', junk);
            return fetch(cartBase() + '/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({ discount: keep.join(',') })
            })
              .catch(function (e) {
                log('撤回失败（忽略）', e && e.message);
              })
              .then(function () {
                return gained.length > 0;
              });
          });
      })
      .catch(function (err) {
        log('失败（已忽略，不影响页面）', err && err.message);
        return false;
      })
      .then(function (gained) {
        running = false;
        /* 抽屉金额/折扣行刷新：走 quick-cart section 自己暴露的 refresh()。
           不只看 gained —— 主题自己的刷新可能比我们的 POST 早一步跑完，
           抽屉里留的是「还没打折」的那版 HTML，所以再按 DOM 与购物车是否一致判一次。 */
        if (opts.afterCartChange && (gained || drawerDiscountStale())) refreshDrawer();
        return gained;
      });
  }

  /**
   * 抽屉 footer 的折扣行是不是「过期」了：
   * 购物车上已有生效折扣但那一行还空着（或反过来）。
   * 只读 DOM + 已经拿到的 sessionStorage 状态，不额外发请求。
   */
  function drawerDiscountStale() {
    var ul = document.querySelector('.quick-cart__footer-discounts');
    if (!ul) return false;
    var hasRow = ul.children.length > 0;
    var hasApplied = storedCodes('applied').length > 0;
    return hasRow !== hasApplied;
  }

  function refreshDrawer() {
    var api = window.AmadaCartDrawer;
    if (!api || typeof api.refresh !== 'function') return;
    try {
      api.refresh();
    } catch (e) {
      log('抽屉刷新失败', e && e.message);
    }
  }

  /* ---------------------------------------------------------- 触发时机 */

  function runOnLoad() {
    var codes = collectCodes();
    remember(codes, 'seen');

    if (isCartPage()) {
      /* 购物车整页没有券条，码来自本会话记录 + 按车里商品补查（10.5 #11） */
      var all = dedupe(codes.concat(storedCodes()));
      applyCodes(all, { resolveFromCart: true }).then(function (gained) {
        /* 服务端渲染的 /cart 不会自己更新，只有「真的新应用上」才刷新一次；
           reload 标记保证同一会话最多刷一次，杜绝死循环。 */
        if (!gained) return;
        var once;
        try {
          once = window.sessionStorage.getItem(RELOAD_KEY);
        } catch (e) {
          once = '1'; /* 存储不可用就不刷，宁可少刷也不要循环 */
        }
        if (once) return;
        try {
          window.sessionStorage.setItem(RELOAD_KEY, '1');
        } catch (e) {
          return;
        }
        window.location.reload();
      });
      return;
    }

    /* 非购物车页：页面上有券条按券条挂；没有券条也按车里商品补查一次
       （车是空的不会发 update.js —— 见 applyCodes 里的空车判断） */
    applyCodes(codes, { resolveFromCart: true });
  }

  /* 加购/改数量之后：购物车刚有货，这是真正把码挂上去的那一次 */
  document.addEventListener('cart:updated', function () {
    var codes = dedupe(collectCodes().concat(storedCodes('applied')));
    /* 让主题自己的抽屉刷新先跑完，避免我们刷出来的 HTML 又被旧响应覆盖 */
    window.setTimeout(function () {
      applyCodes(codes, { afterCartChange: true, resolveFromCart: true });
    }, 300);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runOnLoad, { once: true });
  } else {
    runOnLoad();
  }
})();
